// Per-article scroll-to navigation list.
//
// Mirrors the main editor surface: articles are grouped by category in the
// same fixed editorial order, with each group carrying the category's color
// language (left stripe + ink-colored heading). Clicking an entry expands
// its parent category (if collapsed) and smooth-scrolls the article card
// into view via the parent's onArticleClick handler.

import {
  CATEGORY_ORDER,
  paletteFor,
} from '../lib/categoryPalette.js';

export default function ProgressPanel({ articles = [], onArticleClick }) {
  if (articles.length === 0) {
    return (
      <div className="text-[12px] text-hxd-muted italic">
        No articles in the brief yet.
      </div>
    );
  }

  // Group while preserving CATEGORY_ORDER. Unknown categories get appended
  // at the end so nothing is silently hidden if the pipeline ever ships a
  // category outside the known five.
  const groups = new Map();
  for (const c of CATEGORY_ORDER) groups.set(c, []);
  for (const a of articles) {
    const key = a.category || 'Uncategorized';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(a);
  }
  const entries = Array.from(groups.entries()).filter(
    ([, list]) => list.length > 0
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="font-display text-[11px] font-bold tracking-display-md uppercase text-hxd-muted">
          Articles
        </div>
        <div className="font-display text-[10px] font-bold text-hxd-orange">
          {articles.length}
        </div>
      </div>

      <div className="space-y-3">
        {entries.map(([category, items]) => {
          const palette = paletteFor(category);
          return (
            <div
              key={category}
              className="pl-2"
              style={{ borderLeft: `3px solid ${palette.bar}` }}
            >
              <div className="flex items-center justify-between mb-1 pl-1">
                <span
                  className="font-display text-[10px] font-bold tracking-display-sm uppercase"
                  style={{ color: palette.ink }}
                >
                  {category}
                </span>
                <span className="font-mono text-[10px] text-hxd-muted">
                  {items.length}
                </span>
              </div>
              <ul className="space-y-0.5">
                {items.map((a) => {
                  // Continuous ordinal across the whole brief — matches the
                  // running 01..N numbering on the main article cards (and
                  // the newsletter v2 preview).
                  const globalIndex = articles.indexOf(a);
                  return (
                    <li key={String(a._id)}>
                      <button
                        type="button"
                        onClick={() => onArticleClick?.(a)}
                        className="w-full flex items-center gap-2 text-[12px] text-left text-hxd-ink hover:text-hxd-orange hover:bg-white/60 transition-colors py-1 pl-1 pr-1 rounded-[2px]"
                        title={a.headline || 'Untitled article'}
                      >
                        <span
                          className="font-display text-[10px] font-bold w-6 flex-shrink-0 leading-none"
                          style={{ color: palette.ink }}
                        >
                          {String(globalIndex + 1).padStart(2, '0')}
                        </span>
                        <span className="truncate">
                          {a.headline || 'Untitled article'}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
